import { Message, Modal, Upload } from '@arco-design/web-react';
import { FC } from 'react';
import { getImageMessage } from './UploadTools';
import useMessageStore, { EMessageStatus, EContentType, IMessage } from '@/store/messageStore';
import {
  base64ToFile,
  clone,
  fileToBase64,
  getFileType,
  getLocalTime,
  getVideoSnshotFile,
  sleep,
} from '@/utils';
import { validImage } from '@/utils/valid';
import localforage from 'localforage';
import { localMemory } from 'localmemory';
import { v4 as uuidV4 } from 'uuid';
import { EFaqKnows, useChat, useFaq, useMessageHistory } from '@/service/useChat';
import { useUpload } from './useUpload';
import { useTrackedEffect } from 'ahooks';
import { useUpdateRef } from '@hooks/gHooks';
import { fileToData, MAX_IMAGE_SIZE, MAX_VIDEO_SIZE } from './tools';
import { getImageDimensions, getImageThumbnailFile } from './mediaCompression';
import { errorMsg } from '@/service/serverCode';
import { useCreateChat } from '@/service/useChatConfig';
import { sessionConfigCache } from '@/store/userStore';
import useUserStore from '@/store/userStore';
import useAppStore from '@/store/appStore';
import { useLang } from '@/locales/useLang';

interface IUploadReady {
  file: File;
  isUpload: boolean;
}

const App: FC<{}> = () => {
  const { doUpload, progressNum } = useUpload();
  const messageStore = useMessageStore();
  const { msgSend } = useChat();
  const [uploadTag, setUploadTag] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);
  const [uploadReadyList, forceUpdateUploadReadyList] = useUpdateRef<IUploadReady[]>([]);
  const { createSession } = useCreateChat();
  const { seqInfo, getSessionList } = useMessageHistory();
  const { getFaqKnows } = useFaq();
  const userStore = useUserStore();
  const appStore = useAppStore();
  const $t = useLang();

  /**
   * set the snapshot in message list first
   * @returns return target snapshot message item
   */
  const handleSnapshot = async (file: File): Promise<{ msgPack: IMessage; isImage: boolean }> => {
    const sessionConfig = userStore.sessionConfig;
    const url = URL.createObjectURL(file);
    const type = getFileType(file.name);
    const isImage = validImage(type);

    const seq = messageStore.latestSeq;

    let fileString = url;
    // 这个fileString就是从原图中获取的base64的缩略图
    if (!isImage) {
      // video type
      const snapShotFile = await getVideoSnshotFile(url);
      fileString = await fileToBase64(snapShotFile);
    } else if (isImage) {
      // image type
      const snapShotFile = await getImageThumbnailFile(file);
      fileString = await fileToBase64(snapShotFile);
    } else {
    }

    const id = uuidV4();
    const now = getLocalTime(0).toString();

    const fData = await fileToData(file);

    const msgPack: IMessage = {
      msgBody: fileString,
      msgType: isImage ? EContentType.image : EContentType.video,
      msgSeq: seq,
      status: EMessageStatus.pending,
      senderUid: sessionConfig?.uid || '',
      receiverUid: '',
      createTime: now,
      updateTime: now,
      clientMsgID: id + '-' + now,
      sendTime: now,
      uniqueId: id,
      msgFromTime: 'current',
      progress: 0,
      sessionId: sessionConfig.sessionId,
      fileData: fData,
    };
    messageStore.addMessageList(msgPack);
    return {
      msgPack,
      isImage,
    };
  };

  /**
   * Upload cutouts and source files
   * @param file
   * @param msgPack
   * @param isImage
   */
  const handleUpload = async (file: File, msgPack: IMessage, isImage: boolean) => {
    const { getSessionConfig } = sessionConfigCache();
    const { sessionConfig } = getSessionConfig();

    const msgList =
      (await localforage.getItem<any>(
        sessionConfig?.uid + '__' + sessionConfig?.sessionId + '-msg'
      )) || [];

    msgList.push(msgPack);

    try {
      await localforage.setItem(
        sessionConfig?.uid + '__' + sessionConfig?.sessionId + '-msg',
        msgList
      );
    } catch (error) {}

    const getVideos = await localforage.getItem(
      sessionConfig?.uid + '__' + sessionConfig?.sessionId + '-msg'
    );

    let thumbnail: any = {
      key: '',
      url: '',
    };
    let thumbnailSize = {
      width: 100,
      height: 100,
    };

    const thumbnailbase64 = msgPack.msgBody;

    const cloneMsgpack = clone(msgPack, true);
    // msgPack.msgBody = JSON.stringify({ thumbnail: thumbnail, resource: '' });
    // console.log('cloneMsgpack.msgBody...', cloneMsgpack.msgBody);
    cloneMsgpack.file = file;

    if (!isImage) {
      // video type, get snapshot first
      const ff = await base64ToFile(msgPack.msgBody);
      thumbnailSize = await getImageDimensions(ff);

      // upload the thumbnail then get thumbnail url
      thumbnail = await doUpload(ff).catch(() => {
        const cloneMsgpack = clone(msgPack, true);
        cloneMsgpack.status = EMessageStatus.fail;
        messageStore.updateMessageItem(cloneMsgpack);
      });
    } else {
      const ff = await base64ToFile(msgPack.msgBody);
      thumbnailSize = await getImageDimensions(ff);

      // upload the thumbnail then get thumbnail url
      thumbnail = await doUpload(ff).catch(() => {
        const cloneMsgpack = clone(msgPack, true);
        cloneMsgpack.status = EMessageStatus.fail;
        messageStore.updateMessageItem(cloneMsgpack);
      });
    }

    messageStore.setScrollMessage(msgList);
    cloneMsgpack.msgBody = JSON.stringify({
      thumbnail: thumbnail,
      resource: { url: '', key: '' },
      width: thumbnailSize.width,
      height: thumbnailSize.height,
    });

    cloneMsgpack.thumbnail = JSON.stringify(thumbnail);
    messageStore.updateMessageProgress(cloneMsgpack);

    // debugger
    // messageStore.updateMessageItem(cloneMsgpack);

    const src = await doUpload(
      file,
      true,
      (progress) => {
        cloneMsgpack.progress = progress;
        messageStore.updateMessageProgress(cloneMsgpack);
      },
      (uploadId) => {
        cloneMsgpack.uploadId = uploadId;
        messageStore.updateMessageUploadIdAndThumbnail(cloneMsgpack);

        const tempMsgList = messageStore.getMsgList();
        const targetMsg = tempMsgList.find((item) => {
          return item.uniqueId === msgPack.uniqueId;
        });
        return targetMsg?.isStopUpload || false;
      }
    )
      .then((resp) => {
        const cloneMsgpack = clone(msgPack, true);
        if (!resp) {
          // simple upload fail
          cloneMsgpack.status = EMessageStatus.fail;
          messageStore.updateMessageItem(cloneMsgpack);
        }
        cloneMsgpack.status = EMessageStatus.success;
        return resp;
      })
      .catch(() => {
        const cloneMsgpack = clone(msgPack, true);
        cloneMsgpack.status = EMessageStatus.fail;
        messageStore.updateMessageItem(cloneMsgpack);
        return { key: '', url: '' };
      });

    if (src.url) {
      const resource = isImage
        ? {
            key: src.key,
            url: src.url,
          }
        : {
            key: '',
            url: src.url,
          };

      const doSend = async () => {
        const resp = await msgSend({
          msgBody: JSON.stringify({
            thumbnail: isImage ? thumbnail : thumbnail,
            resource,
            width: thumbnailSize.width,
            height: thumbnailSize.height,
          }),
          msgSeq: cloneMsgpack.msgSeq,
          msgType: cloneMsgpack.msgType,
          clientMsgID: cloneMsgpack.clientMsgID,
          sendTime: cloneMsgpack.sendTime,
        });

        const msgList =
          (await localforage.getItem<any>(
            sessionConfig?.uid + '__' + sessionConfig?.sessionId + '-msg'
          )) || [];

        if (resp.success) {
          const cloneMsgpack = clone(msgPack, true);
          const _latestSessionId = userStore.getSessionConfig()?.sessionId;
          cloneMsgpack.sessionId = _latestSessionId;
          cloneMsgpack.msgBody = JSON.stringify({
            // thumbnail: isImage ? thumbnail : thumbnail,
            thumbnail: thumbnailbase64,
            resource,
            width: thumbnailSize.width,
            height: thumbnailSize.height,
          });
          messageStore.setLatestSeq(resp.data?.msgReq || 0);
          cloneMsgpack.status = EMessageStatus.success;
          cloneMsgpack.msgSeq = resp.data?.msgReq!;
          cloneMsgpack.progress = 100;
          // messageStore.updateMessageItem(msgPack);

          const newList = msgList.map((item: IMessage) => {
            if (cloneMsgpack.uniqueId === item.uniqueId) {
              return cloneMsgpack;
            }
            return item;
          });

          await localforage.setItem(
            sessionConfig?.uid + '__' + sessionConfig?.sessionId + '-msg',
            newList
          );
          messageStore.updateMessageItem(cloneMsgpack);
        } else if (
          [
            errorMsg.sessionError.code,
            errorMsg.sessionFail.code,
            errorMsg.sessionTimeout.code,
          ].includes(resp.code as any)
        ) {
          const resp = await createSession();

          const sessionList = await getSessionList();
          seqInfo.current.sessionList = sessionList;

          if (resp.code === 0) {
            cloneMsgpack.sessionId = resp.data.sessionId;
            messageStore.updateMsgSessionId(cloneMsgpack);
            await doSend();

            await getFaqKnows(EFaqKnows.knows, '');
          }
        } else {
          const cloneMsgpack = clone(msgPack, true);
          const _latestSessionId = userStore.getSessionConfig()?.sessionId;
          cloneMsgpack.sessionId = _latestSessionId;
          cloneMsgpack.status = EMessageStatus.fail;
          messageStore.updateMessageItem(cloneMsgpack);

          const newList = msgList.map((item: IMessage) => {
            if (cloneMsgpack.uniqueId === item.uniqueId) {
              return cloneMsgpack;
            }
            return item;
          });

          console.log('upload....send...', newList);
          await localforage.setItem(
            sessionConfig?.uid + '__' + sessionConfig?.sessionId + '-msg',
            newList
          );

          return resp;
        }
      };

      doSend();
    }
    // console.log('res...', res);
    // console.log('cloneMsgpack....', cloneMsgpack.msgBody);
    // messageStore.updateMessageItem(cloneMsgpack);
  };

  /**
   * Picture or video exceeds size limit
   */
  const isExceedLimitFile = (file: File): boolean => {
    const type = file.type?.split('/')?.[0];

    if (type === 'video' && file.size > MAX_VIDEO_SIZE) {
      Message.error($t('chat_session_status_25'));
      return false;
    }

    if (type === 'image' && file.size > MAX_IMAGE_SIZE) {
      Message.error($t('chat_session_status_24'));
      return false;
    }

    return true;
  };

  useTrackedEffect(
    (changes) => {
      console.log('changes...', changes);
      if (appStore.isKickOff) {
        Message.info($t('chat_session_status_8'));
        return;
      }
      if (changes?.includes(0)) {
        if (!uploadReadyList.current?.length) {
          return;
        }

        // Pending upload queue, control uploads
        const actionUpload = async () => {
          for (let i = 0, len = uploadReadyList.current.length; i < len; i++) {
            const item = uploadReadyList.current[i];
            // Skip if this item has already been uploaded
            if (item.isUpload) {
              continue;
            }

            // Mark as uploaded
            item.isUpload = true;
            uploadReadyList.current[i] = item;

            handleSnapshot(item.file)
              .then(async ({ msgPack, isImage }) => {
                try {
                  handleUpload(item.file, msgPack, isImage);
                } catch (error) {
                  msgPack.status = EMessageStatus.fail;
                  messageStore.updateMessageItem(msgPack);
                }
              })
              .catch((err) => {
                console.log('handleSnapshot...err', err);
              });
          }
        };

        actionUpload();

        // return;
        // handleUpload(cur.file);
      }
    },
    [uploadTag, uploadReadyList.current]
  );

  return (
    <Upload
      key={uploadKey}
      limit={{ maxCount: 9, hideOnExceedLimit: false }}
      multiple
      accept="image/*,video/*"
      showUploadList={false}
      onExceedLimit={() => {
        Message.info($t('chat_session_status_30', { count: 9 }));
      }}
      onChange={() => {
        // force rerender and reset limit maxCount
        setUploadKey((prevKey) => prevKey + 1);
      }}
      beforeUpload={async (file) => {
        const status = isExceedLimitFile(file);
        if (!status) {
          return;
        }

        uploadReadyList.current = [
          ...uploadReadyList.current,
          {
            file: file,
            isUpload: false,
          },
        ];
        forceUpdateUploadReadyList();
        setUploadTag(!uploadTag);
      }}
    >
      <img
        src={require('@img/common/icon-photo.svg')}
        alt=""
        className="w-84 p-12 md:w-42 md:p-4 box-border bg-#fff rounded-full"
      />
    </Upload>
  );
};

export default App;
